/**
 * Calendar constants and configuration.
 */

// Calendar dimensions
export const HOUR_HEIGHT = 60; // pixels per hour

// Time snapping – drag/resize/create operations on the calendar grid
export const SNAP_MINUTES = 5;
export const SNAPS_PER_HOUR = 60 / SNAP_MINUTES;        // 12
export const SNAP_PIXELS = HOUR_HEIGHT / SNAPS_PER_HOUR; // 5px per snap
export const SNAP_HOURS = SNAP_MINUTES / 60;              // 0.0833...

// Picker step – used by DateTimePicker minutesStep in dialogs
export const PICKER_MINUTES = 5;
export const SIDEBAR_WIDTH = 280; // unscheduled sidebar
export const TIME_COLUMN_WIDTH = 60;
export const RESOURCE_COLUMN_WIDTH = 140;

// States the unscheduled sidebar lists (SCHEDULABLE_STATES in scheduling/views.py). A work order
// whose last slot is removed goes back to the sidebar only in these, else the next reload drops it.
export const SIDEBAR_STATES = ['planned', 'in_progress', 'waiting', 'vehicle_in_workshop'];
export const belongsInSidebar = (state) => !state || SIDEBAR_STATES.includes(state);

// Sentinel value for "no employee" / "no service bay" filter option
export const FILTER_NONE = '__none__';

// Week days - use translation keys
// Short names for column headers
export const WEEK_DAY_KEYS = ['calendar.weekday.mon_short', 'calendar.weekday.tue_short', 'calendar.weekday.wed_short', 'calendar.weekday.thu_short', 'calendar.weekday.fri_short'];

// All week days with translation keys
export const ALL_WEEK_DAYS = [
  { index: 0, shortKey: 'calendar.weekday.mon_short', labelKey: 'calendar.weekday.monday' },
  { index: 1, shortKey: 'calendar.weekday.tue_short', labelKey: 'calendar.weekday.tuesday' },
  { index: 2, shortKey: 'calendar.weekday.wed_short', labelKey: 'calendar.weekday.wednesday' },
  { index: 3, shortKey: 'calendar.weekday.thu_short', labelKey: 'calendar.weekday.thursday' },
  { index: 4, shortKey: 'calendar.weekday.fri_short', labelKey: 'calendar.weekday.friday' },
  { index: 5, shortKey: 'calendar.weekday.sat_short', labelKey: 'calendar.weekday.saturday' },
  { index: 6, shortKey: 'calendar.weekday.sun_short', labelKey: 'calendar.weekday.sunday' },
];

// Status colors
// - color: accent color for border
// - bg: light background
// - text: dark readable text color (high contrast)
export const STATUS_CONFIG = {
  quote: {
    labelKey: 'status.quote',
    color: '#805ad5',
    bg: '#f3ecff',
    text: '#4c1d95', // dark violet
  },
  planned: {
    labelKey: 'status.planned',
    color: '#f59e0b',
    bg: '#fef3c7',
    text: '#92400e', // dark amber/brown
  },
  in_progress: {
    labelKey: 'status.in_progress',
    color: '#3b82f6',
    bg: '#dbeafe',
    text: '#1e40af', // dark blue
  },
  completed: {
    labelKey: 'status.completed',
    color: '#10b981',
    bg: '#d1fae5',
    text: '#065f46', // dark green
  },
  picked_up: {
    labelKey: 'status.picked_up',
    color: '#8b5cf6',
    bg: '#ede9fe',
    text: '#5b21b6', // dark purple
  },
  cancelled: {
    labelKey: 'status.cancelled',
    color: '#6b7280',
    bg: '#f3f4f6',
    text: '#374151', // dark gray
  },
  waiting: {
    labelKey: 'status.waiting',
    color: '#ed8936',
    bg: '#fef3e2',
    text: '#9c4221', // dark orange
  },
  vehicle_in_workshop: {
    labelKey: 'status.vehicle_in_workshop',
    color: '#0d9488',
    bg: '#ccfbf1',
    text: '#115e59', // dark teal
  },
};

// Unavailability colors
export const UNAVAILABILITY_CONFIG = {
  vacation: {
    labelKey: 'calendar.absence.vacation',
    color: '#ec4899',
    bg: '#fce7f3',
  },
  sick_leave: {
    labelKey: 'calendar.absence.sick_leave',
    color: '#ef4444',
    bg: '#fee2e2',
  },
  personal: {
    labelKey: 'calendar.absence.personal',
    color: '#6366f1',
    bg: '#e0e7ff',
  },
};

// Calendar note colors
export const CALENDAR_NOTE_CONFIG = {
  color: '#0ea5e9',   // sky blue
  bg: '#e0f2fe',      // light sky blue
  text: '#0c4a6e',    // dark sky blue
};

// UI colors
export const UI_COLORS = {
  selection: '#3b82f6',
  selectionBg: 'rgba(59, 130, 246, 0.15)',
  todayBg: '#eff6ff',
  hourLine: '#e2e8f0',
  halfHourLine: '#f1f5f9',
  currentTimeLine: '#ef4444',
};

// Booking event status config (for online booking events on calendar)
export const BOOKING_STATUS_CONFIG = {
  pending: {
    labelKey: 'booking.status.pending',
    color: '#f97316',     // Orange
    bg: '#fff7ed',
    text: '#9a3412',
    borderStyle: 'dashed',
  },
  confirmed: {
    labelKey: 'booking.status.confirmed',
    color: '#0ea5e9',     // Sky blue (distinct from maintenance blue)
    bg: '#e0f2fe',
    text: '#0c4a6e',
    borderStyle: 'solid',
  },
};

// Break period visual config (e.g., lunch breaks on calendar)
export const BREAK_PERIOD_CONFIG = {
  color: '#94a3b8',
  bg: 'rgba(148, 163, 184, 0.12)',
  text: '#64748b',
};

/**
 * Calculate overlap groups for events on the same day/resource.
 * Returns events with overlapIndex and overlapTotal properties for horizontal positioning.
 */
export function calculateEventOverlaps(dayEvents, startHour, parseISO) {
  if (!dayEvents.length) return [];

  const eventsWithTimes = dayEvents.map((event) => {
    let startMinutes, endMinutes;

    if (event._clampedStartHour !== undefined && event._clampedEndHour !== undefined) {
      startMinutes = event._clampedStartHour * 60;
      endMinutes = event._clampedEndHour * 60;
    } else {
      const start = parseISO(event.start_time);
      const end = parseISO(event.end_time);
      startMinutes = start.getHours() * 60 + start.getMinutes();
      endMinutes = end.getHours() * 60 + end.getMinutes();
    }

    return { ...event, startMinutes, endMinutes };
  }).sort((a, b) => a.startMinutes - b.startMinutes);

  const columns = [];

  return eventsWithTimes.map((event) => {
    let columnIndex = columns.findIndex((colEnd) => event.startMinutes >= colEnd);
    if (columnIndex === -1) {
      columnIndex = columns.length;
      columns.push(event.endMinutes);
    } else {
      columns[columnIndex] = event.endMinutes;
    }
    return { ...event, overlapIndex: columnIndex, overlapTotal: columns.length };
  }).map((event, _, arr) => {
    const overlapping = arr.filter(
      (e) => e.startMinutes < event.endMinutes && e.endMinutes > event.startMinutes
    );
    const maxColumns = Math.max(...overlapping.map((e) => e.overlapIndex + 1));
    return { ...event, overlapTotal: maxColumns };
  });
}

// Duration options for the schedule dialog - use translation keys
export const DURATION_OPTIONS = [
  { value: 0.5, labelKey: 'calendar.duration.30min' },
  { value: 1, labelKey: 'calendar.duration.1h' },
  { value: 1.5, labelKey: 'calendar.duration.1_5h' },
  { value: 2, labelKey: 'calendar.duration.2h' },
  { value: 2.5, labelKey: 'calendar.duration.2_5h' },
  { value: 3, labelKey: 'calendar.duration.3h' },
  { value: 4, labelKey: 'calendar.duration.4h' },
  { value: 5, labelKey: 'calendar.duration.5h' },
  { value: 6, labelKey: 'calendar.duration.6h' },
  { value: 8, labelKey: 'calendar.duration.8h' },
];
